import { Lock } from "lucide-react";
import { useBrand } from "../context/BrandContext";

function scoreColor(score) {
  if (score >= 80) return "#16A34A";
  if (score >= 50) return "#D97706";
  return "#DC2626";
}

function ScoreRing({ score, label, compact }) {
  const r = compact ? 19 : 30;
  const size = compact ? 48 : 76;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score));
  return (
    <div className={`flex items-center gap-3 ${compact ? "" : "pb-4 mb-1 border-b border-black/[0.06]"}`} data-testid="seo-score">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(10,10,10,0.06)" strokeWidth={compact ? 5 : 7} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={scoreColor(pct)} strokeWidth={compact ? 5 : 7} strokeLinecap="round"
          strokeDasharray={`${(pct / 100) * c} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        <text x={size / 2} y={size / 2 + (compact ? 4.5 : 6)} textAnchor="middle" fontSize={compact ? 13 : 18} fontWeight="800" fontFamily="Manrope, sans-serif">
          {Math.round(pct)}
        </text>
      </svg>
      <div>
        <p className={`font-heading font-bold ${compact ? "text-xs" : "text-sm"}`}>{label || "TruSEO Score"}</p>
        <p className={`text-neutral-500 ${compact ? "text-[10px]" : "text-xs"}`}>
          {pct >= 80 ? "Excellent" : pct >= 50 ? "Needs improvement" : "Poor"} &middot; out of 100
        </p>
      </div>
    </div>
  );
}

function Field({ label, value, testId }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="py-2.5 border-b border-black/[0.06] last:border-0">
      <p className="font-mono-accent text-[10px] uppercase tracking-[0.18em] text-neutral-400 mb-1">{label}</p>
      <p data-testid={testId} className="text-xs text-neutral-700 leading-relaxed break-words">{String(value)}</p>
    </div>
  );
}

export function SeoPanel({ seo, compact }) {
  const brand = useBrand();
  if (compact) {
    return (
      <div className="bg-white border border-black/[0.08] rounded-xl p-4" data-testid="seo-panel-compact">
        <div className="flex items-center justify-between mb-2">
          <p className="font-heading font-extrabold text-xs tracking-tight">SEO &middot; All in One SEO</p>
          <span className="inline-flex items-center gap-1 text-[9px] font-mono-accent uppercase tracking-[0.15em] text-neutral-400 border border-black/10 rounded-full px-2 py-0.5">
            <Lock className="h-2.5 w-2.5" /> Read only
          </span>
        </div>
        {!seo ? (
          <p className="text-xs text-neutral-500">No SEO data is available for this article yet.</p>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-start gap-3">
            {typeof seo.score === "number" && (
              <div className="shrink-0">
                <ScoreRing score={seo.score} label={seo.score_type === "keyphrase" ? "Keyphrase Score" : seo.score_type === "headline" ? "Headline Score" : "TruSEO Score"} compact />
              </div>
            )}
            <div className="flex-1 min-w-0 space-y-1.5">
              {seo.title && (
                <p className="text-[11px] text-neutral-700 truncate">
                  <span className="font-mono-accent text-[9px] uppercase tracking-[0.15em] text-neutral-400 mr-1.5">Title</span>
                  {seo.title}
                </p>
              )}
              {seo.description && (
                <p className="text-[11px] text-neutral-700 line-clamp-2">
                  <span className="font-mono-accent text-[9px] uppercase tracking-[0.15em] text-neutral-400 mr-1.5">Meta</span>
                  {seo.description}
                </p>
              )}
              {seo.focus_keyphrase && (
                <p className="text-[11px] text-neutral-700 truncate">
                  <span className="font-mono-accent text-[9px] uppercase tracking-[0.15em] text-neutral-400 mr-1.5">Keyphrase</span>
                  {seo.focus_keyphrase}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="bg-white border border-black/[0.08] rounded-xl p-5" data-testid="seo-panel">
      <div className="flex items-center justify-between mb-4">
        <p className="font-heading font-extrabold text-sm tracking-tight">SEO &middot; All in One SEO</p>
        <span className="inline-flex items-center gap-1 text-[10px] font-mono-accent uppercase tracking-[0.15em] text-neutral-400 border border-black/10 rounded-full px-2 py-0.5">
          <Lock className="h-2.5 w-2.5" /> Read only
        </span>
      </div>
      {!seo ? (
        <p className="text-xs text-neutral-500" data-testid="seo-empty-message">
          No SEO data is available for this article yet.
        </p>
      ) : (
        <>
          {typeof seo.score === "number" ? (
            <ScoreRing score={seo.score} label={seo.score_type === "keyphrase" ? "Keyphrase Score" : seo.score_type === "headline" ? "Headline Score" : "TruSEO Score"} />
          ) : (
            <p className="text-xs text-neutral-400 pb-3 mb-1 border-b border-black/[0.06]" data-testid="seo-score-unavailable">
              TruSEO score is not available for this article yet.
            </p>
          )}
          {typeof seo.headline_score === "number" && seo.score_type !== "headline" && (
            <div className="flex items-center justify-between py-2.5 border-b border-black/[0.06]">
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.18em] text-neutral-400">Headline Score</p>
              <p data-testid="seo-headline-score" className="text-xs font-bold" style={{ color: scoreColor(seo.headline_score) }}>
                {Math.round(seo.headline_score)}/100
              </p>
            </div>
          )}
          <Field label="SEO Title" value={seo.title} testId="seo-title" />
          <Field label="Meta Description" value={seo.description} testId="seo-description" />
          <Field label="Focus Keyphrase" value={seo.focus_keyphrase} testId="seo-focus-keyphrase" />
          <p className="text-[10px] text-neutral-400 mt-3 leading-relaxed">
            SEO tags are managed by the {brand.display_name} team and cannot be edited here.
          </p>
        </>
      )}
    </div>
  );
}
